/** Shared maneuver table sort: one path for pipeline + header clicks (VMG desc when key is null). */

export type ManeuverSortDirection = "asc" | "desc";

export interface ManeuverTableSortState {
  key: string | null;
  direction: ManeuverSortDirection;
}

export function getManeuverSortValue(row: Record<string, unknown>, key: string | null | undefined): number {
  if (key == null || key === "") return Number.NaN;

  if (key === "Datetime") {
    const raw = row.datetime ?? row.Datetime ?? row.DATETIME ?? row[key];
    if (raw == null) return Number.NaN;
    const t = new Date(raw as string | number | Date).getTime();
    return Number.isNaN(t) ? Number.NaN : t;
  }

  const kl = key.toLowerCase();
  let v: unknown = row[key] ?? row[kl] ?? row[key.toUpperCase()];
  if (v != null && v !== "") {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  if (kl === "bsp_min_delta") v = row["Bsp_min_delta"];
  else if (kl === "bsp_min") v = row["Bsp_min"];
  else if (kl === "bsp_max") v = row["Bsp_max"];
  else if (kl === "bsp_entry") v = row["Bsp_entry"];
  else if (kl === "bsp_drop") v = row["Bsp_drop"] ?? row["BSP_DROP"];
  else if (kl === "drop_time") v = row["Drop_time"] ?? row["DROP_TIME"];
  else if (kl === "time_two_boards") v = row["Time_two_boards"] ?? row["TIME_TWO_BOARDS"];
  else v = undefined;

  const n = Number(v);
  return Number.isFinite(n) ? n : Number.NaN;
}

/**
 * Channel the tables rank by before any header is clicked.
 *
 * A takeoff has no meaningful VMG% ranking — it is judged on how hard it built — so it ranks on
 * bsp_build instead.
 *
 * Note the summary table itself no longer shows BUILD BSP (it shows BUILD TIME, the seconds to reach
 * 32 KPH); bsp_build stays the sort key because the compare below is hard-coded descending, and
 * descending is the wrong direction for a time — a fast takeoff has the SMALLEST Build Time. Ranking
 * on the time would mean giving this function a direction as well.
 */
export function getManeuverDefaultSortChannel(eventType?: string | null): string {
  return String(eventType ?? "").trim().toUpperCase() === "TAKEOFF" ? "bsp_build" : "vmg_perc_avg";
}

/** Descending compare on the default channel, rows with no value last. */
function compareByDefaultChannel(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  channel: string
): number {
  const av = getManeuverSortValue(a, channel);
  const bv = getManeuverSortValue(b, channel);
  if (Number.isNaN(av) && Number.isNaN(bv)) return 0;
  if (Number.isNaN(av)) return 1;
  if (Number.isNaN(bv)) return -1;
  return bv - av;
}

export function sortManeuverRows<T extends Record<string, unknown>>(
  rows: T[],
  sortState: ManeuverTableSortState,
  eventType?: string | null
): T[] {
  const copy = [...rows];
  if (sortState.key == null || sortState.key === "") {
    const channel = getManeuverDefaultSortChannel(eventType);
    return copy.sort((a, b) => compareByDefaultChannel(a, b, channel));
  }
  const { key, direction } = sortState;
  return copy.sort((a, b) => {
    const av = getManeuverSortValue(a, key);
    const bv = getManeuverSortValue(b, key);
    if (Number.isNaN(av) && Number.isNaN(bv)) return 0;
    if (Number.isNaN(av)) return 1;
    if (Number.isNaN(bv)) return -1;
    return direction === "asc" ? av - bv : bv - av;
  });
}

/** Display order for TACK column when sorting grouped rows (matches table labels, not raw TWA angle). */
const TACK_DISPLAY_SORT_RANK: Record<string, number> = {
  PORT: 0,
  STBD: 1,
  "S - P": 2,
  "P - S": 3,
};

/**
 * Numeric comparable for grouped (aggregated) maneuver table rows.
 * Uses displayed tack labels when sort key is `twa_entry` (TACK column).
 */
export function getManeuverGroupedNumericSortValue(
  row: Record<string, unknown>,
  key: string | null | undefined
): number {
  if (key == null || key === "") return Number.NaN;

  if (key === "Datetime") {
    return getManeuverSortValue(row, key);
  }

  if (key === "twa_entry") {
    const tack = row.tack;
    const s = tack == null ? "" : String(tack);
    const rank = TACK_DISPLAY_SORT_RANK[s];
    return rank !== undefined ? rank : Number.NaN;
  }

  const kl = key.toLowerCase();
  if (key === "Race_number" || kl === "race_number") {
    const rv = row.Race_number ?? row.race_number ?? row.race;
    if (rv === "TRAINING" || rv === "training") return -1;
    const n = Number(rv);
    return Number.isFinite(n) ? n : Number.NaN;
  }

  return getManeuverSortValue(row, key);
}

/** String fallback for grouped-row sort when values are non-numeric (State, Source, etc.). */
export function getManeuverGroupedStringSortKey(row: Record<string, unknown>, key: string): string {
  const kl = key.toLowerCase();

  if (key === "Datetime" || kl === "datetime") {
    const raw = row.datetime ?? row.Datetime ?? row.DATETIME;
    return raw != null ? String(raw) : "";
  }

  if (key === "twa_entry") {
    return String(row.tack ?? "");
  }

  if (key === "Race_number" || kl === "race_number") {
    const rv = row.Race_number ?? row.race_number ?? row.race;
    if (rv === -1 || rv === "-1" || String(rv) === "-1") return "TRAINING";
    return String(rv ?? "");
  }

  if (key === "State" || kl === "state") {
    return String(row.State ?? row.state ?? row.STATE ?? "");
  }
  if (key === "Config" || kl === "config") {
    return String(row.Config ?? row.config ?? row.CONFIG ?? "");
  }
  if (key === "Crew" || kl === "crew") {
    return String(row.Crew ?? row.crew ?? row.CREW ?? row.CREW_CODE ?? "");
  }
  if (key === "source_name" || kl === "source_name") {
    return String(row.source_name ?? "");
  }
  if (key === "Year" || kl === "year") {
    return String(row.Year ?? row.year ?? row.YEAR ?? "");
  }
  if (key === "Event" || kl === "event") {
    return String(row.Event ?? row.event ?? row.EVENT ?? "");
  }

  const v = row[key] ?? row[kl] ?? row[key.toUpperCase()];
  return v == null ? "" : String(v);
}

/** Sort aggregated group rows so order matches the grouped table display / active column sort. */
export function sortGroupedManeuverRows<T extends Record<string, unknown>>(
  rows: T[],
  sortState: ManeuverTableSortState,
  eventType?: string | null
): T[] {
  const copy = [...rows];
  if (sortState.key == null || sortState.key === "") {
    const channel = getManeuverDefaultSortChannel(eventType);
    return copy.sort((a, b) => compareByDefaultChannel(a, b, channel));
  }

  const { key, direction } = sortState;
  return copy.sort((a, b) => {
    const av = getManeuverGroupedNumericSortValue(a, key);
    const bv = getManeuverGroupedNumericSortValue(b, key);
    const aFin = Number.isFinite(av);
    const bFin = Number.isFinite(bv);

    if (aFin && bFin) {
      if (av !== bv) return direction === "asc" ? av - bv : bv - av;
      return 0;
    }

    // Non-finite / non-numeric values sort after finite ones (same spirit as sortManeuverRows NaN handling).
    if (aFin && !bFin) return -1;
    if (!aFin && bFin) return 1;

    const as = getManeuverGroupedStringSortKey(a, key);
    const bs = getManeuverGroupedStringSortKey(b, key);
    const cmp = as.localeCompare(bs, undefined, { numeric: true, sensitivity: "base" });
    return direction === "asc" ? cmp : -cmp;
  });
}
